
import 'splitpanes/dist/splitpanes.css'

import { ChartBar, DeviceFloppy, LayoutGridAdd, Palette, Refresh, Run } from '@vicons/tabler'
import { Icon } from '@vicons/utils'
import { message } from 'ant-design-vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { indexBy, prop } from 'ramda'
import { Pane, Splitpanes } from 'splitpanes'
import { computed, defineComponent, onMounted, Ref, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import writeXlsxFile from 'write-excel-file'

import CommonAnalysis, {
  EVENTS as COMMON_EVENTS
} from '@/components/analysis/common/CommonAnalysis.vue'
import NetworkList, {
  EVENTS as NET_EVENTS
} from '@/components/analysis/common/network-list/NetworkList.vue'
import WhatIfNodeSelection, {
  EVENTS as VAR_EVENTS
} from '@/components/analysis/common/node-selection/NodeSelectionTable.vue'
import WhatIfTaskList, {
  EVENTS as TASK_EVENTS
} from '@/components/analysis/common/task-list/TaskList.vue'
import WorkspaceInfo from '@/components/analysis/common/workspace-info/WorkspaceInfo.vue'
import useAnalysis from '@/components/analysis/composable/analysis'
import useBase, { TABS } from '@/components/analysis/composable/base'
import useJob from '@/components/analysis/composable/job'
import usePalette from '@/components/analysis/composable/palette'
import useWhatIf, {
  inputNodeMapper,
  outputNodeMapper
} from '@/components/analysis/composable/whatif'
import { AnalysisTask } from '@/components/analysis/libs/common'
import WhatIfApexChart from '@/components/analysis/whatif/WhatIfApexChart.vue'
// import WhatIfChart from '@/components/analysis/whatif/WhatIfChart.vue'
import WhatIfTable, { EVENTS as TABLE_EVENTS } from '@/components/analysis/whatif/WhatIfTable.vue'
import { CATEGORICAL_PALETTES, ColorSchemeItem } from '@/components/common/color-scheme'
import { EMIT_EVENTS } from '@/constants/emits'
import { ROUTE_NAME } from '@/constants/router'
dayjs.extend(relativeTime)
import { logger } from '@/libs/logger'
import { gradientMaker } from '@/libs/utils'
import { cssIcon } from '@/styles/common'
import { JobType } from '@/types/database/job'

import WhatIfBatchImport, { EVENTS as IMPORT_EVENTS } from './WhatIfBatchImport.vue'

const CATEGORICAL_PALETTE_MAP = indexBy(prop('name'), CATEGORICAL_PALETTES)

export default defineComponent({
  components: {
    ChartBar,
    CommonAnalysis,
    DeviceFloppy,
    LayoutGridAdd,
    Run,
    NetworkList,
    Icon,
    Pane,
    Refresh,
    Splitpanes,
    WhatIfBatchImport,
    WhatIfNodeSelection,
    // WhatIfChart,
    WhatIfApexChart,
    WhatIfTable,
    Palette,
    WorkspaceInfo,
    WhatIfTaskList
  },
  setup() {
    const activeTab = ref(TABS.PARAMS)
    const route = useRoute()
    const routerParams = route.params
    let { workspaceId } = routerParams
    if (Array.isArray(workspaceId)) {
      workspaceId = workspaceId[0]
    }
    const selectedTasks = ref(new Set<AnalysisTask>())
    const isBatchImportVisible: Ref<boolean> = ref(false)
    const isParsing: Ref<boolean> = ref(false)

    const table: Ref<any> = ref(null)
    const tableKey: Ref<number> = ref(0)
    const selectionConfig = {
      inputVisible: true,
      utilityVectorVisible: true,
      stateVisible: false,
      variationVisible: false,
      variationsVisible: false,
      constraintVisible: false,
      headers: {
        input: {
          title: 'Input',
          desc: 'Input Node'
        },
        output: {
          title: 'Output',
          desc: 'Output Node'
        }
      }
    }
    const {
      networks,
      loadWorkspace,
      loadNetworks,
      removeNetwork,
      workspace,
      workspaceCreated,
      workspaceModified
    } = useBase(workspaceId)

    const {
      paletteVisible,
      paletteName,
      paletteColors,
      selectPalette,
      showPalettePicker
    } = usePalette()

    const updateTable = () => {
      tableKey.value += 1
      table.value?.update(table.value)
    }

    const onSuccessSaveJob = () => {
      message.success('Analysis job is successfully saved')
    }

    const toggleTask = (task: AnalysisTask) => {
      // task.isSelected = !task.isSelected
      if (task.isSelected) {
        selectedTasks.value.add(task)
      } else {
        selectedTasks.value.delete(task)
      }
    }

    const {
      networkMap,
      currentUser,
      nodeSelections,
      nodeSelectionMap,
      updateNodeSelections,
      inputNodeKeys,
      outputNodeKeys,
      updateInputOutputNodes,
      commonNodeKeys
    } = useAnalysis(workspaceId, JobType.WHATIF, networks)

    const {
      isPersisting,
      abortTask,
      tasks,
      taskMap,
      clearTasks,
      loadJob,
      loadTasks,
      persistJob,
      persistTasks,
      addTask,
      initTasks,
      executeTask,
      removeTask,
      isUpdatingJob,
      isCreatingJob
    } = useJob(
      currentUser,
      workspaceId,
      JobType.WHATIF,
      networks,
      nodeSelections,
      nodeSelectionMap,
      inputNodeMapper,
      outputNodeMapper,
      onSuccessSaveJob
    )

    const {
      clearCharts,
      plotCharts,
      chartData,
      chartNodeIds,
      exportTabular,
      whatIfRows,
      onMoveDownRow,
      onMoveUpRow,
      updateWhatIfRows,
      executeAnalysis,
      preExecuteAnalysis,
      updateRowsWithUtilityVector,
      updateTasksFromWhatIfRows,
      isExecuting,
      maxOutputValue,
      minOutputValue,
      parseWhatIfTasksRaw,
      updateDeterministicValues
    } = useWhatIf(
      workspaceId,
      networkMap,
      nodeSelections,
      nodeSelectionMap,
      inputNodeKeys,
      outputNodeKeys,
      tasks,
      taskMap,
      updateTable,
      onSuccessSaveJob
    )

    const networkColumns = computed(() => {
      return [
        {
          title: 'Network name',
          dataIndex: 'name',
          key: 'name'
        },
        {
          title: 'Nodes count',
          dataIndex: 'nodes.length',
          key: 'nodes'
        }
      ]
    })

    const networkRows = computed(
      () =>
        networks.value?.map((network: any) => ({
          key: network.id,
          nodes: network.nodes,
          name: network.name
        })) || []
    )

    const cardStyle = computed(() => ({
      backgroundSize: '100% 32px',
      backgroundRepeat: 'no-repeat',
      backgroundImage: gradientMaker(workspace.value?.name || 'UNTITLED')
    }))

    const handleLoadNetwork = () => {
      loadNetworks()
    }

    const calculate = () => {
      executeAnalysis()
      updateTable()
    }

    const preCalculate = () => {
      preExecuteAnalysis()
      updateTable()
    }

    const toggleInput = () => {
      updateInputOutputNodes()
      // updateWhatIfRows()
    }
    const toggleOutput = () => {
      updateInputOutputNodes()
      // updateWhatIfRows()
    }

    const init = async () => {
      message.info('Initializing ...')
      initTasks()
      updateInputOutputNodes()
      updateWhatIfRows()
      await preExecuteAnalysis()
      message.success('Initialization completed')
    }

    const onUtilityUpdated = (key: string) => {
      updateRowsWithUtilityVector(key)
    }

    watch(networks, async () => {
      logger.info('watch - networks')
      updateNodeSelections()
      await loadJob()
      await loadTasks()
      updateInputOutputNodes()
      updateWhatIfRows()
    })

    const refresh = async () => {
      await loadTasks()
      updateInputOutputNodes()
      updateWhatIfRows()
      clearCharts()
    }

    watch(nodeSelections, () => {
      updateWhatIfRows()
    })

    onMounted(() => {
      loadWorkspace()
      loadNetworks()
    })

    const saveJob = async () => {
      updateTasksFromWhatIfRows()
      updateDeterministicValues()
      await persistJob()
      await persistTasks()
    }

    const selectTaskPalette = () => {
      paletteColors.value = CATEGORICAL_PALETTE_MAP[paletteName.value]?.items?.map(
        ({ color }: ColorSchemeItem) => color
      )
      paletteVisible.value = false
    }

    const onDuplicateTask = (taskId: string, taskIndex: number) => {
      updateTasksFromWhatIfRows()
      addTask(taskId, taskIndex)
      updateWhatIfRows()
    }

    const onRemoveTask = (taskId: string, taskIndex: number) => {
      updateTasksFromWhatIfRows()
      removeTask(taskId, taskIndex)
      updateWhatIfRows()
    }

    const importBatch = () => {
      isBatchImportVisible.value = true
    }

    const parsingComplete = () => {
      setTimeout(() => {
        isParsing.value = false
      })
    }

    const importBatchOk = async (rawText: string) => {
      isBatchImportVisible.value = false
      isParsing.value = true
      const {
        newTasks,
        error,
        outputNodeKeys: outKeys,
        inputNodeKeys: inKeys
      } = parseWhatIfTasksRaw(rawText)
      if (error) {
        message.error(error)
        parsingComplete()
        return
      }
      clearTasks()
      tasks.value = newTasks
      outputNodeKeys.value = outKeys
      inputNodeKeys.value = inKeys
      updateWhatIfRows()
      parsingComplete()
    }

    const exportResults = async () => {
      const { datas, names } = exportTabular()
      await writeXlsxFile(datas, {
        sheets: names,
        fileName: workspace.value?.name + '-results.xlsx'
      })
    }

    const run = (task: AnalysisTask) => {
      executeTask(task)
    }

    const runAll = async () => {
      message.info('All tasks are submitted to the batch execution')
      await executeTask()
      loadTasks()
    }

    const plotResults = () => {
      const tasks_ = selectedTasks.value?.size ? selectedTasks.value : new Set(tasks.value)
      plotCharts(tasks_)
    }

    const abort = (task: AnalysisTask) => {
      if (!abortTask(task)) {
        message.error('You need to save the job first')
      }
    }

    const removeAll = async () => {
      clearTasks()
      await saveJob()
      updateWhatIfRows()
    }

    const remove = async (taskId: string, taskIndex: number) => {
      removeTask(taskId, taskIndex)
      await saveJob()
      updateWhatIfRows()
    }

    return {
      abort,
      remove,
      removeAll,
      toggleTask,
      run,
      runAll,
      exportResults,
      importBatchOk,
      activeTab,
      importBatch,
      isPersisting,
      isBatchImportVisible,
      calculate,
      cardStyle,
      CATEGORICAL_PALETTE_MAP,
      CATEGORICAL_PALETTES,
      chartData,
      chartNodeIds,
      COMMON_EVENTS,
      commonNodeKeys,
      cssIcon,
      EMIT_EVENTS,
      handleLoadNetwork,
      init,
      refresh,
      isParsing,
      inputNodeKeys,
      isCreatingJob,
      isExecuting,
      isUpdatingJob,
      loadNetworks,
      maxOutputValue,
      minOutputValue,
      NET_EVENTS,
      TASK_EVENTS,
      networkColumns,
      networkRows,
      networkMap,
      networks,
      nodeSelections,
      onDuplicateTask,
      onMoveDownRow,
      plotResults,
      onMoveUpRow,
      onRemoveTask,
      onUtilityUpdated,
      outputNodeKeys,
      paletteColors,
      paletteName,
      paletteVisible,
      preCalculate,
      removeNetwork,
      ROUTE_NAME,
      saveJob,
      selectionConfig,
      selectPalette,
      selectTaskPalette,
      showPalettePicker,
      IMPORT_EVENTS,
      TABLE_EVENTS,
      table,
      tableKey,
      TABS,
      tasks,
      toggleInput,
      toggleOutput,
      VAR_EVENTS,
      whatIfRows,
      workspace,
      workspaceCreated,
      workspaceId,
      workspaceModified
    }
  }
})
